import React, { useMemo } from 'react';
import { Modal } from '../../ui/Modal';
import { ChevronRight } from 'lucide-react';
import type { ScheduleStrategySummary } from '../../../types/schedule';
import { formatTimeRange, formatTradingType } from '../../../utils/scheduleSummary';
import PnlFlash from '../../common/PnlFlash';
import { StatusBoxRow } from '../../common/StatusBoxRow';
import { expiryTypeLabel } from '../../../utils/strategy/expiryDisplay';
import { BasketModalHeader } from '../shared/BasketModalHeader';
import { StrategyActionToolbar } from '../shared/StrategyActionToolbar';
import BrokerBreakdownLine from '../shared/BrokerBreakdownLine';

interface ProBasketModalProps {
  basketId: string;
  basketName: string;
  summaries: ScheduleStrategySummary[];
  onClose: () => void;
  onOpenStrategy: (groupKey: string) => void;
  /** When provided, renders an "Exit All" button that exits every open position in the basket. */
  onExitBasket?: (basketId: string) => void;
  /** When provided, renders a "Retry All" button that retries every rejected entry across the basket's strategies. */
  onRetryBasket?: (basketId: string) => void;
  /** When provided, renders a "Terminate" button that platform-only stop-watches every non-terminal position in the basket (never calls the broker). */
  onTerminateBasket?: (basketId: string) => void;
  /** Disables the bulk action buttons and shows a spinner while an in-flight action is running. */
  actionPending?: boolean;
  /** When provided, renders a refresh icon in the header that re-fetches the schedule summary. */
  onRefresh?: () => void;
  refreshing?: boolean;
}

function keyOf(s: ScheduleStrategySummary): string {
  return `${s.basket_id}#${s.strategy_id}`;
}

/**
 * Pro basket modal — card grid for strategies instead of a nested table.
 * Shares its header + status ribbon with the classic basket modal; the body
 * is what differs between the two.
 */
const ProBasketModal: React.FC<ProBasketModalProps> = ({
  basketId, basketName, summaries, onClose, onOpenStrategy, onExitBasket,
  onRetryBasket, onTerminateBasket, actionPending = false, onRefresh, refreshing = false,
}) => {

  const sorted = useMemo(
    () => [...summaries].sort((a, b) => {
      const at = a.scheduled_entry_time || a.scheduled_exit_time || '99:99';
      const bt = b.scheduled_entry_time || b.scheduled_exit_time || '99:99';
      if (at !== bt) return at.localeCompare(bt);
      return a.strategy_name.localeCompare(b.strategy_name);
    }),
    [summaries],
  );

  // Basket-level aggregates. Only broker rejections drive the red fill-ribbon
  // variant and the Retry All button. Cancellations are informational and
  // surface as a separate annotation in the ribbon.
  let legTotal = 0, filled = 0, pending = 0;
  let rejected = 0, cancelled = 0;
  let openCount = 0, closedCount = 0;
  let realized = 0, unrealized = 0;
  let slPlaced = 0, slRejected = 0, slPending = 0;
  const underlyings = new Set<string>();
  const tradingTypes = new Set<string>();
  for (const s of summaries) {
    legTotal    += s.leg_count;
    filled      += s.entry_counts.complete;
    pending     += s.entry_counts.pending + s.entry_counts.open;
    rejected    += s.entry_counts.rejected;
    cancelled   += s.entry_counts.cancelled;
    openCount   += s.open_count;
    closedCount += s.closed_count;
    realized    += s.realized_pnl;
    unrealized  += s.unrealized_pnl;
    slPlaced    += s.sl_counts.placed;
    slRejected  += s.sl_counts.rejected;
    slPending   += s.sl_counts.pending;
    if (s.underlying) underlyings.add(s.underlying);
    if (s.trading_type) tradingTypes.add(s.trading_type);
  }
  const retryable = rejected;

  const pills: string[] = [];
  underlyings.forEach((u) => pills.push(u));
  tradingTypes.forEach((t) => pills.push(t));

  return (
    <Modal
      isOpen
      onClose={onClose}
      maxWidth="5xl"
      ariaLabel={`${basketName} schedule`}
    >
        <BasketModalHeader
          basketName={basketName}
          strategyCount={sorted.length}
          legTotal={legTotal}
          pills={pills}
          filled={filled}
          pending={pending}
          rejected={rejected}
          cancelled={cancelled}
          openCount={openCount}
          closedCount={closedCount}
          realized={realized}
          unrealized={unrealized}
          slPlaced={slPlaced}
          slPending={slPending}
          slRejected={slRejected}
          onClose={onClose}
          onRefresh={onRefresh}
          refreshing={refreshing}
        />

        <StrategyActionToolbar
          retryableCount={retryable}
          exitableCount={openCount}
          onRetryAll={onRetryBasket
            ? () => onRetryBasket(basketId)
            : undefined}
          onExitAll={onExitBasket
            ? () => onExitBasket(basketId)
            : undefined}
          pending={actionPending}
        />

        {/* Body: strategy cards grid */}
        <div className="flex-1 overflow-auto p-3 sm:p-4 bg-gray-50/50 dark:bg-gray-900/30">
          {sorted.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No strategies scheduled for this basket on this day.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {sorted.map((s) => (
                <StrategyCard
                  key={keyOf(s)}
                  summary={s}
                  onClick={() => onOpenStrategy(keyOf(s))}
                />
              ))}
            </div>
          )}
        </div>
    </Modal>
  );
};

const StrategyCard: React.FC<{
  summary: ScheduleStrategySummary;
  onClick: () => void;
}> = ({ summary: s, onClick }) => {
  const time = formatTimeRange(
    s.scheduled_entry_time,
    s.scheduled_exit_time,
    s.intraday_exit_mode,
  );
  const brokerLabel = s.brokers.length === 0
    ? null
    : s.brokers.length === 1 ? s.brokers[0] : `${s.brokers.length} brokers`;

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate flex items-center gap-1 group-hover:text-blue-600 dark:group-hover:text-blue-400">
            {s.strategy_name}
            <ChevronRight className="w-3 h-3 opacity-40 group-hover:opacity-100" />
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums mt-0.5">
            {time}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {s.underlying}
            <span className="ml-1 text-[10px] uppercase text-gray-400 dark:text-gray-500 whitespace-nowrap">
              {expiryTypeLabel(s.expiry_type)}
              {s.product_type && ` · ${s.product_type}`}
            </span>
            {s.trading_type && (
              <>
                <span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {formatTradingType(s.trading_type, s.intraday_exit_mode)}
                </span>
              </>
            )}
            <span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
            {s.leg_count} leg{s.leg_count !== 1 ? 's' : ''}
            <span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
            {s.total_effective_lots ?? s.total_lots}L
          </div>
          <div className="mt-1">
            <BrokerBreakdownLine items={s.broker_breakdown ?? []} />
          </div>
        </div>
        <PnlFlash value={s.total_pnl} className="text-base font-bold shrink-0" />
      </div>

      <div className="mt-3">
        <StatusBoxRow summary={s} />
      </div>

      {brokerLabel && (
        <div className="mt-2 text-[10px] text-gray-400 dark:text-gray-500" title={s.brokers.join(' · ')}>
          {brokerLabel}
        </div>
      )}
    </button>
  );
};

export default ProBasketModal;
